import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@monorepo/utils/apiRequest';
import { checkAuth } from '@monorepo/auth/Actions';
import { Button, Card, Input, Label, FieldError, Hint } from '@monorepo/common/components/ui';

/**
 * Signup, reached from an invite email.
 *
 * Signup is invite-gated (SIGNUP_REQUIRE_INVITE on the backend), so this page
 * exists to give the link in that email somewhere to land. Before it, an
 * invite was a code an admin read out over Slack and a person typed into
 * nothing — there was no signup screen in the app at all.
 *
 * The code and the address both arrive in the query string. Prefilling the
 * address is not a convenience: an invite restricted to an email is REJECTED
 * at signup if the address does not match, and the error ("Invite is for a
 * different email") cannot say which address was meant without leaking it to
 * whoever is holding the link. So the invited address is filled in and the
 * field is left editable, for the case where the invite carries no
 * restriction at all.
 *
 * Not lazy, and deliberately: this is a cold start on someone's phone from a
 * link in an email, which is the worst moment to ask for a second round trip
 * for a chunk — the same reason the OAuth callbacks stay eager.
 */

const TURNSTILE_SITE_KEY = import.meta.env?.VITE_TURNSTILE_SITE_KEY || '';

/**
 * The backend's password policy, mirrored.
 *
 * `SignUpSchema.validate_password` enforces all five of these, and webargs
 * rejects a failing payload with a 422 BEFORE the view runs — so the person
 * gets no field-level message at all, just a failed signup on the one screen
 * where they cannot retry from anywhere else.
 *
 * Duplicating a rule is a thing to avoid in general; leaving someone stuck on
 * a signup page is worse. If the policy changes on the server, it changes
 * here — the symptom of drift is a 422, which the handler below names
 * explicitly rather than showing raw HTML.
 *
 * The symbol set is the schema's, character for character.
 */
const SYMBOLS = '!@#$%^&*()-_=+{}[]|;:,.<>?/~';
const PASSWORD_RULES = [
  ['at least 6 characters', (v) => v.length >= 6],
  ['an uppercase letter', (v) => /[A-Z]/.test(v)],
  ['a lowercase letter', (v) => /[a-z]/.test(v)],
  ['a number', (v) => /[0-9]/.test(v)],
  ['a symbol', (v) => [...v].some((c) => SYMBOLS.includes(c))],
];

const missingFrom = (value) =>
  PASSWORD_RULES.filter(([, ok]) => !ok(value)).map(([label]) => label);

/** "a number" / "a number and a symbol" / "a, b and c" */
const listed = (items) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const OddsignalsLogo = () => (
  <h1 className="text-h4 text-ink">
    oddsignals<span className="text-ink-subtle">.ai</span>
  </h1>
);

/**
 * The Turnstile widget, rendered only when a site key is configured.
 *
 * The backend SKIPS verification when TURNSTILE_SECRET_KEY is unset and
 * REQUIRES a token when it is set, so the two halves have to match: a site
 * key here with no secret there is a pointless challenge, and a secret there
 * with no site key here fails every signup with "Bot verification failed"
 * and no way for the person to do anything about it.
 */
const Turnstile = ({ onToken }) => {
  const box = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let widgetId;
    let cancelled = false;

    const render = () => {
      if (cancelled || !box.current || !window.turnstile) return;
      widgetId = window.turnstile.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'dark',
        callback: onToken,
        // A token is good for 300s. Clearing it on expiry means the button
        // reports "not ready" rather than the server rejecting a stale one.
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      const existing = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
      const script = existing || Object.assign(document.createElement('script'), {
        src: TURNSTILE_SRC, async: true, defer: true,
      });
      script.addEventListener('load', render);
      if (!existing) document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      try {
        if (widgetId !== undefined) window.turnstile?.remove(widgetId);
      } catch (e) { /* the script may not have loaded at all */ }
    };
  }, [onToken]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={box} className="flex justify-center" />;
};

/**
 * What to show when signup fails.
 *
 * The backend's own message is the useful one wherever there is one — it
 * distinguishes an expired invite from a used one from an address that does
 * not match, and each wants a different response from the person reading it.
 *
 * A 422 is the exception. That is webargs rejecting the payload against the
 * schema before the view runs, and it renders Flask's default HTML error
 * page — so `data.error` is absent and the "message" is a document. Showing
 * that verbatim puts raw markup in the error box on the one screen someone
 * cannot navigate away from and come back to.
 */
const signupError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (status === 422) {
    // flask-apispec attaches field messages when its handler is installed;
    // use them when they are there and fall back to the policy when not.
    const messages = data?.messages?.json || data?.messages;
    if (messages && typeof messages === 'object') {
      const fields = Object.values(messages).flat().filter((m) => typeof m === 'string');
      if (fields.length) return fields.join(' ');
    }
    return 'Some of those details were not accepted. Check the password rules '
         + 'below, and that the email address is valid.';
  }

  if (typeof data?.error === 'string') return data.error;
  // Never surface an HTML body as a message.
  if (typeof err?.message === 'string' && !err.message.trim().startsWith('<')) {
    return err.message;
  }
  return 'Could not create your account. Please try again.';
};

const Signup = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const inviteCode = params.get('invite') || params.get('code') || '';
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: params.get('email') || '',
    password: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const missing = missingFrom(form.password);
    if (missing.length) {
      setError(`Your password needs ${listed(missing)}.`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/users/signup', {
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        invite_code: inviteCode || undefined,
        turnstile_token: turnstileToken || undefined,
      });
      // Signup logs you in SERVER-side — it sets the session — but the store
      // still holds the anonymous state it booted with, and the dashboard is
      // behind that state. Navigating without re-reading it bounces straight
      // back to /login, having just created the account. Measured: it did.
      await dispatch(checkAuth());
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(signupError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <div style={{ height: 'var(--safe-top)' }} />
      <div
        className="flex flex-1 items-center justify-center overflow-y-auto overscroll-contain px-4 py-8"
        style={{ paddingBottom: 'calc(2rem + var(--safe-bottom))' }}
      >
        <Card size="lg" className="w-full max-w-md space-y-7">
          <div className="text-center">
            <div className="mb-4"><OddsignalsLogo /></div>
            <p className="mt-2 text-body-sm text-ink-muted">
              {inviteCode ? 'Create your account' : 'Create your account with an invite'}
            </p>
          </div>

          {!inviteCode && (
            <div className="rounded-field border border-line bg-surface-sunken p-3 text-body-sm text-ink-muted">
              This page needs an invite link. Open the link from your invite
              email, or ask whoever invited you to send it again.
            </div>
          )}

          {error && (
            <div className="rounded-field border border-danger bg-danger-tint p-3 text-body-sm text-danger">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="first_name">First name</Label>
                <Input id="first_name" autoComplete="given-name" required
                       value={form.first_name} onChange={set('first_name')} />
              </div>
              <div>
                <Label htmlFor="last_name">Last name</Label>
                <Input id="last_name" autoComplete="family-name" required
                       value={form.last_name} onChange={set('last_name')} />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required
                     value={form.email} onChange={set('email')} />
              {params.get('email') && (
                <Hint>Your invite was sent to this address.</Hint>
              )}
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="new-password" required
                     minLength={6} value={form.password} onChange={set('password')} />
              <Hint>
                At least 6 characters, with an uppercase letter, a lowercase
                letter, a number and a symbol.
              </Hint>
            </div>

            <Turnstile onToken={setTurnstileToken} />

            <Button type="submit" variant="primary" size="lg" className="w-full"
                    disabled={submitting || !inviteCode}>
              {submitting ? 'Creating your account…' : 'Create account'}
            </Button>
            {!inviteCode && (
              <FieldError>An invite link is required to sign up.</FieldError>
            )}
          </form>

          {/* The floor under `(pointer: coarse)` covers buttons and
              `a[role=button]`, not a bare inline <a> — forcing 44px on links
              inside sentences would wreck every paragraph in the app. So this
              one is a Button, which carries the height itself. */}
          <div className="flex flex-col items-center gap-1">
            <p className="text-body-sm text-ink-muted">Already have an account?</p>
            <Button as={Link} to="/login" variant="ghost" size="sm">Sign in</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
